import {
  TourProvider,
  StepType as BasicStepType,
  Position,
  PopoverContentProps,
  useTour as BaseUseTour,
} from '@reactour/tour';
import '@reactour/popover/dist/index.css';
import React from 'react';
// @ts-ignore
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Button } from '@tigergraph/app-ui-lib/button';
import { Delete } from 'baseui/icon';

type Props = {
  steps: StepType[];
  children: React.ReactNode;
  position?: Position;
  disableInteraction?: boolean;
  onClose?: () => void;
};

export type StepType = BasicStepType & {
  title?: string;
  content: React.ReactNode;
  hideButton?: boolean;
};

export function useTour() {
  const base = BaseUseTour();
  return base;
}

export function MultiStepTourProvider({ steps, children, position, disableInteraction, onClose }: Props) {
  const [css, theme] = useStyletron();
  return (
    <TourProvider
      steps={steps}
      afterOpen={(e) => {
        if (e) {
          disableBodyScroll(e as HTMLElement);
        }
      }}
      beforeClose={(e) => {
        if (e) {
          enableBodyScroll(e as HTMLElement);
        }
      }}
      position={position}
      disableInteraction={disableInteraction}
      disableKeyboardNavigation={true}
      showBadge={false}
      highlightedMaskClassName="tour-mask"
      styles={{
        popover: (base, state) => ({
          ...base,
          padding: '12px',
          borderRadius: '5px',
          // @ts-ignore
          ...doArrow(state.position, state.verticalAlign, state.horizontalAlign, theme.colors['background.primary']),
          backgroundColor: theme.colors['background.primary'],
        }),
        // @ts-ignore
        highlightedArea: (base) => ({
          ...base,
          // known issue:
          // the top layer have opacity 0.7, so the color will be darker
          stroke: theme.colors['border.selected'],
          strokeWidth: '4px',
          display: 'block',
          pointerEvents: 'none',
          rx: '4px',
        }),
        // @ts-ignore
        maskArea: (base) => ({ ...base, rx: '4px' }),
        close: (base) => ({
          ...base,
          left: 'auto',
          right: 8,
          top: 8,
          outline: 'none',
          lineHeight: 0,
          background: 'none',
        }),
      }}
      onClickMask={() => {}}
      ContentComponent={(props) => <ContentComponent {...props} onClose={onClose} />}
    >
      {children}
    </TourProvider>
  );
}

function ContentComponent(props: PopoverContentProps & { onClose?: () => void }) {
  const { currentStep, steps, setSteps, setCurrentStep, setIsOpen, onClose } = props;
  const step = steps[currentStep] as StepType;

  const [css, theme] = useStyletron();

  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
  };

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        maxWidth: '296px',
      })}
    >
      <div
        className={css({
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        })}
      >
        <div
          className={css({
            fontWeight: '600',
            lineHeight: '16px',
            marginRight: 'auto',
          })}
        >
          {step.title}
        </div>
        <Button kind="text" size="compact" shape="square" onClick={handleClose}>
          <Delete color={theme.colors['icon.primary']} />
        </Button>
      </div>
      <div className={css({ lineHeight: '16px' })}>{step.content}</div>
      {/* <DotAnimation /> */}
      <div
        className={css({
          display: 'flex',
          gap: '8px',
          padding: '4px 0',
        })}
      >
        <div
          className={css({
            marginRight: 'auto',
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
          })}
        >
          {steps.map((_, index) => (
            <div
              key={index}
              className={css({
                width: '5px',
                height: '5px',
                borderRadius: '50%',
                backgroundColor:
                  index === currentStep
                    ? theme.colors['background.selected.bold']
                    : theme.colors['background.accent.neutral.subtler'],
              })}
            />
          ))}
        </div>
        {step.hideButton ? null : (
          <>
            <Button size="compact" kind="primary">
              {currentStep === steps.length - 1 ? 'Got it' : 'Next'}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

const opositeSide = {
  top: 'bottom',
  bottom: 'top',
  right: 'left',
  left: 'right',
} as const;

type Pos = 'top' | 'bottom' | 'left' | 'right' | 'custom';

function doArrow(position: Pos, verticalAlign: Pos, horizontalAlign: Pos, color: string) {
  if (!position || position === 'custom') {
    return {};
  }

  const width = 16;
  const height = 12;
  const isVertical = position === 'top' || position === 'bottom';
  const spaceFromSide = 10;

  const obj = {
    // @ts-ignore
    [`--rtp-arrow-${isVertical ? opositeSide[horizontalAlign] : verticalAlign}`]: height + spaceFromSide + 'px',
    [`--rtp-arrow-${opositeSide[position]}`]: -height + 2 + 'px',
    [`--rtp-arrow-border-${isVertical ? 'left' : 'top'}`]: `${width / 2}px solid transparent`,
    [`--rtp-arrow-border-${isVertical ? 'right' : 'bottom'}`]: `${width / 2}px solid transparent`,
    [`--rtp-arrow-border-${position}`]: `${height}px solid ${color}`,
  };
  return obj;
}

// todo refer: https://codepen.io/maxzz/pen/GvMzjJ
export function DotAnimation() {
  const [css, theme] = useStyletron();
  return (
    <div
      style={{
        position: 'relative',
        width: '16px',
        height: '16px',
      }}
    >
      <div
        style={{
          backgroundColor: theme.colors['background.brand.bold'],
          borderRadius: `50%`,
          boxSizing: 'border-box',
          display: 'block',
          height: '6px',
          position: 'absolute',
          top: '5px',
          left: '5px',
          transformOrigin: `center center`,
          width: '6px',
        }}
      />
      <div
        style={{
          animation: `1.2s ease-in-out 0s infinite normal none running joyride-beacon-outer`,
          backgroundColor: theme.colors['background.brand.bold'],
          borderRadius: `50%`,
          boxSizing: 'border-box',
          display: 'block',
          height: '16px',
          opacity: 0.6,
          position: 'absolute',
          transformOrigin: `center center`,
          width: '16px',
        }}
      />
    </div>
  );
}
